/**
 * FaqView.tsx — AInnov8 FAQ chatbot module.
 *
 * Renders a full-height chat interface that streams questions to the
 * /api/faq/chat endpoint (OpenAI Responses API + vector store).
 */
import { useCallback, useEffect, useRef, useState } from 'react'

// ── Types ─────────────────────────────────────────────────────────────────────

interface Citation {
  filename: string
  file_id: string
}

type Role = 'user' | 'assistant'

interface Message {
  id: string
  role: Role
  content: string
  citations?: Citation[]
  error?: boolean
}

// ── API ───────────────────────────────────────────────────────────────────────

interface ChatPayload {
  question: string
  history: { role: Role; content: string }[]
}

interface ChatResult {
  answer: string
  citations: Citation[]
}

async function askFaq(payload: ChatPayload): Promise<ChatResult> {
  const res = await fetch('/api/faq/chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    let detail = `HTTP ${res.status}`
    try {
      const b = await res.json()
      detail = b?.detail ?? b?.message ?? detail
    } catch {}
    throw new Error(detail)
  }
  return res.json()
}

// ── Sub-components ────────────────────────────────────────────────────────────

function TypingIndicator() {
  return (
    <div className="flex items-end gap-3 animate-fade-up">
      {/* Avatar */}
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-teal-600 flex items-center justify-center text-white text-xs font-bold">
        AI
      </div>
      <div className="bg-white border border-slate-100 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
        <span className="flex gap-1 items-center h-4">
          <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:0ms]" />
          <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:150ms]" />
          <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:300ms]" />
        </span>
      </div>
    </div>
  )
}

function CitationPill({ citation, index }: { citation: Citation; index: number }) {
  const name = citation.filename || citation.file_id || `Source ${index + 1}`
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5
                 rounded-full bg-teal-50 text-teal-700 border border-teal-200
                 cursor-default select-none"
    >
      <svg width="9" height="9" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <rect x="1" y="1" width="10" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
        <path d="M3 4h6M3 6.5h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      </svg>
      {name}
    </span>
  )
}

function ChatBubble({ msg }: { msg: Message }) {
  const isUser = msg.role === 'user'
  return (
    <div
      className={`flex items-end gap-3 animate-fade-up ${isUser ? 'flex-row-reverse' : ''}`}
    >
      {/* Avatar */}
      {isUser ? (
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-navy-900 flex items-center justify-center text-white text-xs font-semibold">
          You
        </div>
      ) : (
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-teal-600 flex items-center justify-center text-white text-xs font-bold">
          AI
        </div>
      )}

      <div className={`max-w-[72%] space-y-1.5 ${isUser ? 'items-end flex flex-col' : ''}`}>
        {/* Bubble */}
        <div
          className={`
            px-4 py-3 text-sm leading-relaxed shadow-sm
            ${isUser
              ? 'bg-navy-900 text-white rounded-2xl rounded-br-sm'
              : msg.error
                ? 'bg-red-50 border border-red-200 text-red-800 rounded-2xl rounded-bl-sm'
                : 'bg-white border border-slate-100 text-slate-800 rounded-2xl rounded-bl-sm'
            }
          `}
        >
          {/* Render newlines as <br> */}
          {msg.content.split('\n').map((line, i, arr) => (
            <span key={i}>
              {line}
              {i < arr.length - 1 && <br />}
            </span>
          ))}
        </div>

        {/* Citations */}
        {!isUser && msg.citations && msg.citations.length > 0 && (
          <div className="flex flex-wrap gap-1 pl-0.5">
            {msg.citations.map((c, i) => (
              <CitationPill key={i} citation={c} index={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center px-6 select-none">
      <div className="w-14 h-14 rounded-2xl bg-teal-50 border border-teal-100 flex items-center justify-center text-2xl mb-4">
        💬
      </div>
      <h2 className="text-base font-semibold text-slate-800 mb-1">AInnov8 FAQ</h2>
      <p className="text-sm text-slate-400 max-w-xs leading-relaxed">
        Ask anything — answers come straight from your knowledge base.
      </p>
    </div>
  )
}

// ── Main view ─────────────────────────────────────────────────────────────────

export default function FaqView() {
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput]       = useState('')
  const [loading, setLoading]   = useState(false)
  const bottomRef               = useRef<HTMLDivElement>(null)
  const textareaRef             = useRef<HTMLTextAreaElement>(null)

  // Auto-scroll to latest message
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  // Auto-grow textarea
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value)
    const el = e.target
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`
  }

  const sendMessage = useCallback(async () => {
    const question = input.trim()
    if (!question || loading) return

    // Optimistically add user bubble
    const userMsg: Message = { id: crypto.randomUUID(), role: 'user', content: question }
    setMessages(prev => [...prev, userMsg])
    setInput('')
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }
    setLoading(true)

    try {
      // Build history from all prior messages (excluding the one just added)
      const history = messages.map(m => ({ role: m.role, content: m.content }))
      const result  = await askFaq({ question, history })

      const assistantMsg: Message = {
        id:        crypto.randomUUID(),
        role:      'assistant',
        content:   result.answer || '(no answer returned)',
        citations: result.citations,
      }
      setMessages(prev => [...prev, assistantMsg])
    } catch (err) {
      const errMsg: Message = {
        id:      crypto.randomUUID(),
        role:    'assistant',
        content: err instanceof Error ? err.message : 'An unexpected error occurred.',
        error:   true,
      }
      setMessages(prev => [...prev, errMsg])
    } finally {
      setLoading(false)
    }
  }, [input, loading, messages])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const clearChat = () => {
    setMessages([])
    setInput('')
  }

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)]">

      {/* ── Toolbar ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-5 py-2 border-b border-slate-100 bg-white flex-shrink-0">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-teal-600 text-white text-[10px] font-bold">
            AI
          </span>
          <span className="text-sm font-medium text-slate-700">AInnov8 FAQ</span>
          <span className="text-xs text-slate-400">&middot; powered by OpenAI</span>
        </div>
        <button
          onClick={clearChat}
          disabled={messages.length === 0 && !input}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium
                     border border-slate-200 text-slate-600 bg-white
                     hover:bg-slate-50 hover:border-slate-300 hover:text-slate-800
                     active:scale-95 transition-all
                     disabled:opacity-30 disabled:cursor-not-allowed disabled:scale-100"
          title="Start a new conversation"
        >
          <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path d="M6 1v5M1 6h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            <path d="M11 6A5 5 0 1 1 6 1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
          New chat
        </button>
      </div>

      {/* ── Message list ─────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-5 bg-slate-50">
        <div className="max-w-3xl mx-auto space-y-5">
          {messages.length === 0 && !loading && <EmptyState />}

          {messages.map(msg => (
            <ChatBubble key={msg.id} msg={msg} />
          ))}

          {loading && <TypingIndicator />}

          <div ref={bottomRef} />
        </div>
      </div>

      {/* ── Input bar ────────────────────────────────────────────────────── */}
      <div className="flex-shrink-0 border-t border-slate-100 bg-white px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-end gap-3">
          <textarea
            ref={textareaRef}
            rows={1}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Ask a question… (Enter to send, Shift+Enter for new line)"
            disabled={loading}
            className="flex-1 resize-none rounded-xl border border-slate-200 bg-white px-4 py-3
                       text-sm text-slate-800 placeholder-slate-400 leading-relaxed
                       focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent
                       disabled:opacity-50 disabled:cursor-not-allowed
                       max-h-40 overflow-y-auto"
          />
          <button
            onClick={sendMessage}
            disabled={!input.trim() || loading}
            aria-label="Send"
            className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-xl
                       bg-teal-600 text-white shadow-sm
                       hover:bg-teal-700 active:scale-95 transition-all
                       disabled:opacity-40 disabled:cursor-not-allowed disabled:scale-100"
          >
            {loading ? (
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="40 60" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M14 2L2 7.5l5 1.5 1.5 5L14 2z" fill="currentColor" />
              </svg>
            )}
          </button>
        </div>
        <p className="max-w-3xl mx-auto mt-1.5 text-[10px] text-slate-400 px-1">
          Shift+Enter for a new line · answers are grounded in your knowledge base
        </p>
      </div>
    </div>
  )
}
